import { useState } from "react";
import { CheckCircle2, Flame, IdCard, Lightbulb, Plus, RefreshCw, Star, Target, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { Btn, Field, Modal, Page, Spinner, TopBar } from "@/components/kit";
import { useCompleteGoal, useCreateGoal, useDeleteGoal, useGoalHistory, useGoals } from "@/hooks/queries";
import { fmtDate } from "@/lib/toto";

const META = {
  networking: { icon: IdCard, fg: "#2563EB", bg: "#EFF6FF", label: "Networking" },
  connections: { icon: Users, fg: "#059669", bg: "#ECFDF5", label: "Connections" },
  follow_up: { icon: RefreshCw, fg: "#EA580C", bg: "#FFF7ED", label: "Follow-ups" },
  lead_generation: { icon: Target, fg: "#7C3AED", bg: "#F5F3FF", label: "Leads" },
};
const meta = (c) => META[c] ?? { icon: Star, fg: "#0000EC", bg: "#E5E5FD", label: "Goal" };

function Ring({ pct, done, total }) {
  const r = 52, c = 2 * Math.PI * r;
  return (
    <div className="relative w-[120px] h-[120px] shrink-0">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#E5E5FD" strokeWidth="11" />
        <circle cx="60" cy="60" r={r} fill="none" stroke="#0000EC" strokeWidth="11" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(pct, 1))} style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-[24px] font-extrabold">{Math.round(pct * 100)}%</span><span className="text-[12px] text-[#64748B]">{done}/{total} done</span></div>
    </div>
  );
}

function GoalItem({ g }) {
  const complete = useCompleteGoal();
  const del = useDeleteGoal();
  const m = meta(g.category);
  const pct = g.target ? Math.min(1, g.progress / g.target) : 0;
  return (
    <div className="t-card p-4 flex items-center gap-3" data-testid={`goal-${g.key}`}>
      <span className="w-11 h-11 rounded-[12px] flex items-center justify-center shrink-0" style={{ background: m.bg }}><m.icon className="w-5 h-5" style={{ color: m.fg }} /></span>
      <div className="flex-1 min-w-0">
        <p className={`text-[15px] font-bold ${g.completed ? "text-[#059669]" : ""}`}>{g.title}</p>
        <div className="flex items-center gap-2 mt-1.5">
          <div className="flex-1 h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct * 100}%`, background: g.completed ? "#059669" : m.fg }} /></div>
          <span className="text-[12px] font-semibold text-[#64748B]">{Math.min(g.progress, g.target)}/{g.target}</span>
        </div>
      </div>
      {g.completed ? <CheckCircle2 className="w-6 h-6 text-[#059669]" /> : g.manual && (
        <button className="h-9 px-3 rounded-full bg-[#E5E5FD] text-[#0000EC] text-[13px] font-bold" onClick={() => complete.mutate(g.key, { onSuccess: () => toast.success("Goal completed ✓") })} data-testid={`goal-complete-${g.key}`}>Mark done</button>
      )}
      {g.custom && <button className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[#FEF2F2]" onClick={() => del.mutate(g.key.replace(/^custom:/, ""), { onSuccess: () => toast.success("Goal deleted."), onError: (e) => toast.error(e.message) })} data-testid={`goal-delete-${g.key}`}><Trash2 className="w-4 h-4 text-[#DC2626]" /></button>}
    </div>
  );
}

function CreateGoal({ open, onClose }) {
  const create = useCreateGoal();
  const [f, setF] = useState({ title: "", period: "daily", target: "1", category: "networking" });
  const submit = async () => {
    if (!f.title.trim()) return toast.error("Give your goal a title.");
    try {
      await create.mutateAsync({ title: f.title.trim(), period: f.period, target: Math.max(1, parseInt(f.target, 10) || 1), category: f.category });
      toast.success("Goal added.");
      onClose();
    } catch (e) { toast.error(e.message); }
  };
  const sel = (k, opts) => (
    <select className="t-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`goal-form-${k}`}>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
  );
  return (
    <Modal open={open} onClose={onClose} title="New custom goal" testid="create-goal-modal">
      <div className="flex flex-col gap-4">
        <Field label="Title" placeholder="e.g. Call 3 new leads" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} testid="goal-form-title" />
        <div className="grid grid-cols-2 gap-3">
          <div><span className="t-label">Period</span>{sel("period", [["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]])}</div>
          <Field label="Target" type="number" min="1" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} testid="goal-form-target" />
        </div>
        <div><span className="t-label">Category</span>{sel("category", Object.entries(META).map(([k, v]) => [k, v.label]))}</div>
        <Btn onClick={submit} loading={create.isPending} testid="goal-form-submit">Add Goal</Btn>
      </div>
    </Modal>
  );
}

export default function Goals() {
  const { data, isLoading } = useGoals();
  const history = useGoalHistory().data ?? [];
  const [period, setPeriod] = useState("daily");
  const [creating, setCreating] = useState(false);
  if (isLoading) return <Spinner />;
  const list = data?.goals?.[period] ?? [];
  const daily = data?.goals?.daily ?? [];
  const done = daily.filter((g) => g.completed).length;
  const s = data?.streak ?? {};
  return (
    <Page>
      <TopBar title="Goals" right={<button className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F1F5F9]" onClick={() => setCreating(true)} data-testid="goals-add"><Plus className="w-6 h-6" /></button>} />
      <div className="grid gap-4 lg:grid-cols-2 mt-2">
        <div className="t-card p-5 flex items-center gap-5" data-testid="goals-progress">
          <Ring pct={daily.length ? done / daily.length : 0} done={done} total={daily.length} />
          <div><p className="text-[18px] font-extrabold">Today's progress</p><p className="text-[14px] text-[#475569] mt-1">{data?.message}</p></div>
        </div>
        <div className="rounded-[18px] p-5 text-white bg-gradient-to-r from-[#F59E0B] to-[#EA580C] flex items-center gap-4" data-testid="goals-streak">
          <Flame className="w-12 h-12 fill-white/30" />
          <div className="flex-1"><p className="text-[32px] font-extrabold leading-none">{s.currentStreak ?? 0} <span className="text-[16px] font-bold">day streak</span></p><p className="text-[14px] text-white/90 mt-1">Longest: {s.longestStreak ?? 0} days</p></div>
        </div>
      </div>
      <div className="grid grid-cols-3 p-1 rounded-[14px] bg-[#F1F5F9] mt-6 max-w-md">
        {["daily", "weekly", "monthly"].map((p) => <button key={p} onClick={() => setPeriod(p)} data-testid={`goals-tab-${p}`} className={`h-10 rounded-[11px] text-[14px] font-semibold capitalize ${period === p ? "bg-white text-[#0000EC] shadow-sm" : "text-[#475569]"}`}>{p}</button>)}
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">{list.map((g) => <GoalItem key={g.key} g={g} />)}</div>
      {!list.length && <p className="mt-4 text-[14px] text-[#64748B]">No {period} goals yet.</p>}
      <div className="mt-6 rounded-[18px] bg-[#FEF9C3] p-4 flex gap-3"><Lightbulb className="w-5 h-5 text-[#B45309] shrink-0" /><p className="text-[14px] text-[#B45309]">Scanning cards, saving contacts and completing follow-ups update your goals automatically.</p></div>
      <h2 className="t-section mt-8 mb-3">History</h2>
      <div className="t-card divide-y divide-[#E2E8F0]" data-testid="goals-history">
        {history.slice(0, 20).map((h, i) => <div key={i} className="flex justify-between px-4 py-3 text-[14px]"><span className="font-medium">{h.title}</span><span className="text-[#64748B]">{fmtDate(h.date)}</span></div>)}
        {!history.length && <p className="px-4 py-4 text-[14px] text-[#64748B]">Completed goals will show up here.</p>}
      </div>
      <CreateGoal open={creating} onClose={() => setCreating(false)} />
    </Page>
  );
}
